/**
 * PyramidDSL Types (Browser-compatible)
 */

export interface SourceLocation {
  line: number;
  column: number;
  offset: number;
}

export interface SourceSpan {
  start: SourceLocation;
  end: SourceLocation;
}

export enum TokenType {
  STRING = "STRING",
  NUMBER = "NUMBER",
  BOOLEAN = "BOOLEAN",
  NULL = "NULL",
  COLOR = "COLOR",
  IDENTIFIER = "IDENTIFIER",
  SCREEN = "SCREEN",
  DATA = "DATA",
  TYPE = "TYPE",
  IF = "IF",
  ELSE = "ELSE",
  FOR_EACH = "FOR_EACH",
  IMPORT = "IMPORT",
  EXPORT = "EXPORT",
  LBRACE = "LBRACE",
  RBRACE = "RBRACE",
  LPAREN = "LPAREN",
  RPAREN = "RPAREN",
  LBRACKET = "LBRACKET",
  RBRACKET = "RBRACKET",
  COLON = "COLON",
  COMMA = "COMMA",
  DOT = "DOT",
  ARROW = "ARROW",
  QUESTION = "QUESTION",
  DOLLAR = "DOLLAR",
  AT = "AT",
  EQUALS = "EQUALS",
  DOUBLE_EQUALS = "DOUBLE_EQUALS",
  NOT_EQUALS = "NOT_EQUALS",
  LESS_THAN = "LESS_THAN",
  GREATER_THAN = "GREATER_THAN",
  LESS_EQUALS = "LESS_EQUALS",
  GREATER_EQUALS = "GREATER_EQUALS",
  AND = "AND",
  OR = "OR",
  NOT = "NOT",
  PLUS = "PLUS",
  MINUS = "MINUS",
  STAR = "STAR",
  SLASH = "SLASH",
  PERCENT = "PERCENT",
  DOUBLE_QUESTION = "DOUBLE_QUESTION",
  EOF = "EOF",
  ERROR = "ERROR",
}

export interface Token {
  type: TokenType;
  value: string;
  span: SourceSpan;
}

export interface ASTNode {
  kind: string;
  span: SourceSpan;
}

export interface PyramidFile extends ASTNode {
  kind: "PyramidFile";
  imports: ImportDeclaration[];
  types: TypeDeclaration[];
  screens: ScreenDeclaration[];
}

export interface ImportDeclaration extends ASTNode {
  kind: "ImportDeclaration";
  path: string;
  items: string[];
}

export interface ScreenDeclaration extends ASTNode {
  kind: "ScreenDeclaration";
  name: string;
  route?: string;
  dataBlock?: DataBlock;
  body: ComponentNode;
}

export interface TypeDeclaration extends ASTNode {
  kind: "TypeDeclaration";
  name: string;
  fields: TypeField[];
}

export interface TypeField extends ASTNode {
  kind: "TypeField";
  name: string;
  type: TypeAnnotation;
  optional: boolean;
  defaultValue?: Expression;
}

export type TypeAnnotation =
  | PrimitiveType
  | CustomType
  | ArrayType
  | MapType
  | OptionalType;

export interface PrimitiveType extends ASTNode {
  kind: "PrimitiveType";
  name: "String" | "Int" | "Float" | "Bool" | "Color" | "Date" | "DateTime";
}

export interface CustomType extends ASTNode {
  kind: "CustomType";
  name: string;
}

export interface ArrayType extends ASTNode {
  kind: "ArrayType";
  elementType: TypeAnnotation;
}

export interface MapType extends ASTNode {
  kind: "MapType";
  keyType: TypeAnnotation;
  valueType: TypeAnnotation;
}

export interface OptionalType extends ASTNode {
  kind: "OptionalType";
  innerType: TypeAnnotation;
}

export interface DataBlock extends ASTNode {
  kind: "DataBlock";
  fields: DataField[];
}

export interface DataField extends ASTNode {
  kind: "DataField";
  name: string;
  type: TypeAnnotation;
  defaultValue?: Expression;
}

export interface ComponentNode extends ASTNode {
  kind: "ComponentNode";
  type: string;
  props: PropValue[];
  children: (ComponentNode | IfNode | ForEachNode)[];
  slots: SlotNode[];
}

export interface PropValue extends ASTNode {
  kind: "PropValue";
  name: string;
  value: Expression;
}

export interface SlotNode extends ASTNode {
  kind: "SlotNode";
  name: string;
  children: (ComponentNode | IfNode | ForEachNode)[];
}

export interface IfNode extends ASTNode {
  kind: "IfNode";
  condition: Expression;
  thenBranch: (ComponentNode | IfNode | ForEachNode)[];
  elseBranch?: (ComponentNode | IfNode | ForEachNode)[];
}

export interface ForEachNode extends ASTNode {
  kind: "ForEachNode";
  iterable: Expression;
  itemName: string;
  indexName?: string;
  keyExpr?: Expression;
  body: (ComponentNode | IfNode | ForEachNode)[];
}

export type Expression =
  | LiteralExpr
  | IdentifierExpr
  | MemberExpr
  | CallExpr
  | BinaryExpr
  | UnaryExpr
  | ConditionalExpr
  | InterpolatedStringExpr
  | ArrayExpr
  | ObjectExpr
  | ActionExpr
  | ActionChainExpr;

export interface LiteralExpr extends ASTNode {
  kind: "LiteralExpr";
  value: string | number | boolean | null;
  literalType: "string" | "number" | "boolean" | "null" | "color";
}

export interface IdentifierExpr extends ASTNode {
  kind: "IdentifierExpr";
  name: string;
}

export interface MemberExpr extends ASTNode {
  kind: "MemberExpr";
  object: Expression;
  property: string;
  optional: boolean;
}

export interface CallExpr extends ASTNode {
  kind: "CallExpr";
  callee: Expression;
  arguments: Expression[];
}

export interface BinaryExpr extends ASTNode {
  kind: "BinaryExpr";
  operator: string;
  left: Expression;
  right: Expression;
}

export interface UnaryExpr extends ASTNode {
  kind: "UnaryExpr";
  operator: string;
  operand: Expression;
}

export interface ConditionalExpr extends ASTNode {
  kind: "ConditionalExpr";
  condition: Expression;
  thenExpr: Expression;
  elseExpr: Expression;
}

export interface InterpolatedStringExpr extends ASTNode {
  kind: "InterpolatedStringExpr";
  parts: (string | Expression)[];
}

export interface ArrayExpr extends ASTNode {
  kind: "ArrayExpr";
  elements: Expression[];
}

export interface ObjectExpr extends ASTNode {
  kind: "ObjectExpr";
  properties: { key: string; value: Expression }[];
}

export interface ActionExpr extends ASTNode {
  kind: "ActionExpr";
  actionType: string;
  arguments: Expression[];
  namedArguments: { name: string; value: Expression }[];
}

export interface ActionChainExpr extends ASTNode {
  kind: "ActionChainExpr";
  actions: ActionExpr[];
}

export interface CompilerOutput {
  success: boolean;
  screens: CompiledScreen[];
  errors: CompilerError[];
  warnings: CompilerWarning[];
}

export interface CompiledScreen {
  name: string;
  route?: string;
  data: CompiledDataSchema;
  layout: JsonNode;
}

export interface CompiledDataSchema {
  fields: {
    name: string;
    type: string;
    optional: boolean;
    default?: unknown;
  }[];
}

export interface JsonNode {
  type: string;
  props?: Record<string, unknown>;
  children?: JsonNode[];
  slots?: Record<string, JsonNode[]>;
  condition?: string;
}

export interface CompilerError {
  message: string;
  code: string;
  span: SourceSpan;
  severity: "error";
}

export interface CompilerWarning {
  message: string;
  code: string;
  span: SourceSpan;
  severity: "warning";
}
